import { ArrowPathIcon } from "@heroicons/react/24/outline"
import { useMemo, useState } from "react"
import { toast } from "react-toastify"
import {
  AgendaSlotDto,
  AgendaSlotType,
  useAgendaControllerAddLinkMutation,
} from "~/ui-rtk/api/agendaApi"
import {
  AdBlastJobResult,
  JobRunView,
  useListJobsQuery,
} from "~/ui-rtk/api/jobsApi"
import { Button } from "~/ui-rtk/components/ui/controls"
import MultiSelect, {
  type TMultiSelectOption,
} from "~/ui-rtk/components/ui/controls/MultiSelect"
import useDate from "~/ui-rtk/hooks/date"
import {
  budgetChangeLabel,
  BudgetUpdateResult,
  describeJob,
} from "~/ui-rtk/pages/Jobs/jobViews"
import { cn } from "~/ui-rtk/utils/tailwind-utils"

/** Job states that can still contribute to a slot (excludes failed/cancelled/rejected). */
const LINKABLE_STATUSES = new Set([
  "COMPLETED",
  "PENDING",
  "QUEUED",
  "PROCESSING",
  "SCHEDULED",
  "AWAITING_APPROVAL",
])

/** Which job type fulfills each slot type, and how a run reads in the picker. */
export const SLOT_JOB_CONFIG: Partial<
  Record<
    AgendaSlotType,
    {
      jobType: string
      noun: string
      completedLabel: (job: JobRunView) => string
      /** Second dropdown line: what the run actually did (source, names…). */
      describe: (job: JobRunView) => string | undefined
    }
  >
> = {
  AD_BLAST: {
    jobType: "ad-blast",
    noun: "blast jobs",
    completedLabel: job => {
      const summary = (job.result as AdBlastJobResult | null)?.summary
      return `${summary?.successful ?? "?"}/${summary?.total ?? "?"} ads created`
    },
    describe: job => {
      const result = job.result as AdBlastJobResult | null
      // e.g. `ad 120208… → 4 ad sets` / `2 sources → 8 ads`
      const source = describeJob(job)
      const firstAdName = result?.results?.find(row => row.success)?.result
        ?.newAdName
      return (
        [source, firstAdName ? `“${firstAdName}”` : null]
          .filter(Boolean)
          .join(" · ") || undefined
      )
    },
  },
  AD_CREATIVE: {
    jobType: "ad-creative",
    noun: "creative builds",
    completedLabel: () => "creative saved",
    describe: job => {
      const name = (job.result as { name?: string } | null)?.name
      return name ? `“${name}”` : undefined
    },
  },
  BUDGET_RECOMMENDATION: {
    jobType: "budget-update",
    noun: "budget updates",
    completedLabel: job =>
      budgetChangeLabel(job.result as BudgetUpdateResult | null) ??
      "budget updated",
    describe: job => {
      const result = job.result as BudgetUpdateResult | null
      return result
        ? `${result.entity ?? "entity"} ${result.entityId ?? ""}`
        : undefined
    },
  },
}

function jobUnits(job: JobRunView): number {
  const summary = (job.result as AdBlastJobResult | null)?.summary
  return summary?.successful || summary?.total || 1
}

/**
 * Multi-select of job runs (blasts / creative builds, per slot type) created
 * inside the slot's date window, so work done outside the agenda flow can be
 * attached to the slot after the fact. Completed runs link as DELIVERED with
 * their real unit count; still-running ones link as PENDING and deliver via
 * the job completion hook.
 */
export default function LinkJobsPicker({ slot }: { slot: AgendaSlotDto }) {
  const dayjs = useDate()
  const [selected, setSelected] = useState<TMultiSelectOption[]>([])
  const [addLink, { isLoading: isLinking }] =
    useAgendaControllerAddLinkMutation()

  const config = SLOT_JOB_CONFIG[slot.type]

  const window = useMemo(
    () => ({
      from: dayjs(slot.scheduledDate).startOf("day").toISOString(),
      to: dayjs(slot.endDate ?? slot.scheduledDate)
        .endOf("day")
        .toISOString(),
    }),
    [slot.scheduledDate, slot.endDate, dayjs],
  )

  const {
    data: jobs = [],
    isLoading,
    isFetching,
    refetch,
  } = useListJobsQuery(
    {
      type: config?.jobType,
      from: window.from,
      to: window.to,
      take: 100,
    },
    { skip: !config },
  )

  const linkedJobIds = useMemo(
    () => new Set(slot.links.map(link => link.jobRunId).filter(Boolean)),
    [slot.links],
  )

  const options = useMemo<TMultiSelectOption[]>(
    () =>
      jobs
        .filter(
          job => LINKABLE_STATUSES.has(job.status) && !linkedJobIds.has(job.id),
        )
        .map(job => {
          const outcome =
            job.status === "COMPLETED" && config
              ? config.completedLabel(job)
              : job.status.toLowerCase().replace(/_/g, " ")
          return {
            value: job.id,
            label: `${dayjs(job.enqueuedAt).format("MMM D, HH:mm")} · ${outcome}`,
            description: config?.describe(job),
          }
        }),
    [jobs, linkedJobIds, dayjs, config],
  )

  const handleLink = async () => {
    const byId = new Map(jobs.map(job => [job.id, job]))
    try {
      for (const option of selected) {
        const job = byId.get(option.value)
        if (!job) continue
        const adCreativeId = (job.result as { adCreativeId?: string } | null)
          ?.adCreativeId
        await addLink({
          id: slot.id,
          createAgendaSlotLinkDto: {
            jobRunId: job.id,
            ...(adCreativeId ? { adCreativeId } : {}),
            units: jobUnits(job),
            // Running jobs link as PENDING and flip to DELIVERED via the
            // job completion hook.
            status: job.status === "COMPLETED" ? "DELIVERED" : "PENDING",
          },
        }).unwrap()
      }
      toast.success(
        selected.length === 1
          ? "Job linked to slot"
          : `${selected.length} jobs linked to slot`,
      )
      setSelected([])
    } catch {
      toast.error("Failed to link jobs to slot")
    }
  }

  if (!config) return null

  const refreshButton = (
    <button
      type="button"
      className="btn btn-ghost btn-sm btn-square"
      title="Refresh jobs"
      aria-label="Refresh jobs"
      disabled={isFetching}
      onClick={() => refetch()}
    >
      <ArrowPathIcon className={cn("h-4 w-4", isFetching && "animate-spin")} />
    </button>
  )

  if (!isLoading && options.length === 0) {
    return (
      <div className="flex items-center justify-between gap-2">
        <p className="text-xs text-system-gray-500">
          No unlinked {config.noun} ran between{" "}
          {dayjs(window.from).format("MMM D")} and{" "}
          {dayjs(window.to).format("MMM D")}.
        </p>
        {refreshButton}
      </div>
    )
  }

  return (
    <div className="flex items-center gap-2">
      <div className="min-w-0 flex-1">
        <MultiSelect
          options={options}
          value={selected}
          // Escape the dialog's scroll container — the panel overlays the
          // modal instead of forcing a scroll inside it.
          portal
          placeholder={
            isLoading ? "Loading jobs…" : `Select ${config.noun} to link…`
          }
          onChange={setSelected}
        />
      </div>
      {refreshButton}
      <Button
        variant={{ variant: "solid", color: "blue" }}
        size="sm"
        type="button"
        disabled={selected.length === 0 || isLinking}
        isLoading={isLinking}
        onClick={handleLink}
      >
        Link{selected.length > 0 ? ` (${selected.length})` : ""}
      </Button>
    </div>
  )
}
